import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { 
  Plus, TrendingUp, TrendingDown, DollarSign, ArrowUpCircle, ArrowDownCircle,
  Banknote, CreditCard, ArrowLeftRight, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function FinanzasAdminPage() {
  const { getAuthHeader } = useAuth();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [periodo, setPeriodo] = useState('mes'); // mes, anio, global
  const [finanzas, setFinanzas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'ingreso', categoria: 'otros', monto: 0, descripcion: '', fecha: format(now, 'yyyy-MM-dd'), forma_pago: 'efectivo',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params for finanzas list
      let finanzasUrl = `${API_URL}/finanzas`;
      if (periodo === 'mes') {
        const start = `${selectedYear}-${String(selectedMonth).padStart(2,'0')}-01`;
        const endM = selectedMonth === 12 ? 1 : selectedMonth + 1;
        const endY = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
        const end = `${endY}-${String(endM).padStart(2,'0')}-01`;
        finanzasUrl += `?fecha_inicio=${start}&fecha_fin=${end}`;
      } else if (periodo === 'anio') {
        finanzasUrl += `?fecha_inicio=${selectedYear}-01-01&fecha_fin=${selectedYear + 1}-01-01`;
      }
      // else global = no filters

      const [finRes, resRes] = await Promise.all([
        axios.get(finanzasUrl, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/finanzas/resumen?mes=${selectedMonth}&anio=${selectedYear}&periodo=${periodo}`, { headers: getAuthHeader() }),
      ]);
      setFinanzas(finRes.data);
      setResumen(resRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar finanzas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedMonth, selectedYear, periodo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/finanzas`, formData, { headers: getAuthHeader() });
      toast.success('Transaccion registrada');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error al registrar');
    }
  };

  const prevMonth = () => {
    if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(selectedYear - 1); }
    else setSelectedMonth(selectedMonth - 1);
  };
  const nextMonth = () => {
    if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(selectedYear + 1); }
    else setSelectedMonth(selectedMonth + 1);
  };

  const periodoLabel = periodo === 'mes' ? `${meses[selectedMonth - 1]} ${selectedYear}` : periodo === 'anio' ? `Ano ${selectedYear}` : 'Todo el tiempo';

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="finanzas-admin-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-3xl font-bold text-allegra-dark">Finanzas</h1>
          <p className="text-xs sm:text-sm text-allegra-gray">Control de ingresos y egresos</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-full gap-1.5 h-9 text-xs sm:text-sm sm:h-10" data-testid="add-finanza-btn">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Nueva Transaccion</span>
          <span className="sm:hidden">Nueva</span>
        </Button>
      </div>

      {/* Period Tabs */}
      <div className="flex flex-col gap-3">
        <div className="flex rounded-xl bg-muted p-1 gap-1" data-testid="periodo-tabs">
          {[{v:'mes',l:'Mes'},{v:'anio',l:'Ano'},{v:'global',l:'Global'}].map(t => (
            <button key={t.v} onClick={() => setPeriodo(t.v)}
              className={`flex-1 text-xs sm:text-sm font-medium py-2 rounded-lg transition-all ${periodo === t.v ? 'bg-white shadow text-allegra-dark' : 'text-allegra-gray hover:text-allegra-dark'}`}
              data-testid={`tab-${t.v}`}>
              {t.l}
            </button>
          ))}
        </div>

        {/* Month/Year navigation */}
        {periodo === 'mes' && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium text-allegra-dark min-w-[140px] text-center">{meses[selectedMonth - 1]} {selectedYear}</span>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
        {periodo === 'anio' && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedYear(selectedYear - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium text-allegra-dark">{selectedYear}</span>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedYear(selectedYear + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div></div>
      ) : (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-3 gap-2.5 sm:gap-4" data-testid="finanzas-resumen">
            <Card className="border-0 shadow-md">
              <CardContent className="p-3 sm:p-5 text-center">
                <TrendingUp className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="font-heading text-lg sm:text-2xl font-bold text-green-600 truncate">${resumen?.total_ingresos?.toLocaleString() || 0}</p>
                <p className="text-[10px] sm:text-xs text-allegra-gray mt-0.5">Ingresos</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-3 sm:p-5 text-center">
                <TrendingDown className="w-5 h-5 text-red-500 mx-auto mb-1" />
                <p className="font-heading text-lg sm:text-2xl font-bold text-red-500 truncate">${resumen?.total_egresos?.toLocaleString() || 0}</p>
                <p className="text-[10px] sm:text-xs text-allegra-gray mt-0.5">Egresos</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardContent className="p-3 sm:p-5 text-center">
                <DollarSign className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <p className={`font-heading text-lg sm:text-2xl font-bold truncate ${(resumen?.balance || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>${resumen?.balance?.toLocaleString() || 0}</p>
                <p className="text-[10px] sm:text-xs text-allegra-gray mt-0.5">Balance</p>
              </CardContent>
            </Card>
          </div>

          {/* Payment method breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <ArrowUpCircle className="w-4 h-4 text-green-500" /> Ingresos por forma de pago
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2">
                  {[{k:'efectivo',icon:Banknote,label:'Efectivo'},{k:'transferencia',icon:ArrowLeftRight,label:'Transferencia'},{k:'tarjeta',icon:CreditCard,label:'Tarjeta'}].map(({k,icon:Icon,label}) => (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-allegra-gray" /><span className="text-allegra-gray">{label}</span></div>
                      <span className="font-bold text-green-600">${(resumen?.ingresos_por_forma_pago?.[k] || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <ArrowDownCircle className="w-4 h-4 text-red-500" /> Egresos por forma de pago
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2">
                  {[{k:'efectivo',icon:Banknote,label:'Efectivo'},{k:'transferencia',icon:ArrowLeftRight,label:'Transferencia'},{k:'tarjeta',icon:CreditCard,label:'Tarjeta'}].map(({k,icon:Icon,label}) => (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-allegra-gray" /><span className="text-allegra-gray">{label}</span></div>
                      <span className="font-bold text-red-500">${(resumen?.egresos_por_forma_pago?.[k] || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Transactions list */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
              <CardTitle className="text-sm sm:text-lg font-heading">
                Transacciones - {periodoLabel}
                <span className="text-allegra-gray font-normal text-xs ml-2">({finanzas.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-4 sm:pb-6">
              {finanzas.length === 0 ? (
                <p className="text-allegra-gray text-sm text-center py-8">No hay transacciones en este periodo</p>
              ) : (
                <>
                  {/* Mobile cards */}
                  <div className="sm:hidden space-y-2">
                    {finanzas.map((f) => (
                      <div key={f.id} className="p-3 bg-muted/50 rounded-xl" data-testid={`finanza-card-${f.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-allegra-dark truncate">{f.descripcion}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-allegra-gray">{f.fecha}</span>
                              <Badge variant="outline" className="text-[10px] h-5">{f.categoria}</Badge>
                              <Badge variant="outline" className="text-[10px] h-5">{f.forma_pago || 'efectivo'}</Badge>
                            </div>
                          </div>
                          <span className={`font-bold text-sm whitespace-nowrap ${f.tipo === 'ingreso' ? 'text-green-600' : 'text-red-500'}`}>
                            {f.tipo === 'ingreso' ? '+' : '-'}${f.monto?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Fecha</th>
                          <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Tipo</th>
                          <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Categoria</th>
                          <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Descripcion</th>
                          <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Forma Pago</th>
                          <th className="text-right py-3 px-3 text-sm font-medium text-allegra-gray">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {finanzas.map((f) => (
                          <tr key={f.id} className="border-b last:border-0">
                            <td className="py-3 px-3 text-sm">{f.fecha}</td>
                            <td className="py-3 px-3">
                              <Badge className={f.tipo === 'ingreso' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{f.tipo}</Badge>
                            </td>
                            <td className="py-3 px-3 text-sm capitalize">{f.categoria}</td>
                            <td className="py-3 px-3 text-sm max-w-[200px] truncate">{f.descripcion}</td>
                            <td className="py-3 px-3 text-sm capitalize">{f.forma_pago || 'efectivo'}</td>
                            <td className={`py-3 px-3 text-sm text-right font-bold ${f.tipo === 'ingreso' ? 'text-green-600' : 'text-red-500'}`}>
                              {f.tipo === 'ingreso' ? '+' : '-'}${f.monto?.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* New Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Nueva Transaccion</DialogTitle>
            <DialogDescription>Registra un ingreso o egreso manual</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ingreso">Ingreso</SelectItem>
                    <SelectItem value="egreso">Egreso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="renta">Renta</SelectItem>
                    <SelectItem value="transporte">Transporte</SelectItem>
                    <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                    <SelectItem value="combustible">Combustible</SelectItem>
                    <SelectItem value="publicidad">Publicidad</SelectItem>
                    <SelectItem value="otros">Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <Input value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} placeholder="Descripcion de la transaccion" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Monto</Label>
                <Input type="number" min={0} value={formData.monto} onChange={(e) => setFormData({ ...formData, monto: Number(e.target.value) })} required data-testid="finanza-monto" />
              </div>
              <div className="space-y-2">
                <Label>Forma de Pago</Label>
                <Select value={formData.forma_pago} onValueChange={(v) => setFormData({ ...formData, forma_pago: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input type="date" value={formData.fecha} onChange={(e) => setFormData({ ...formData, fecha: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-finanza-btn">Registrar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
